/**
 * BillingEndDateInlineEditor — แก้ "วันสิ้นสุดเก็บเงิน" ของสัญญาที่จบแล้ว แบบ inline บนหน้ารายละเอียดการเช่า
 *
 * feedback amintra 29 ก.ย. 2026 (RNT-20260716-0051): บัญชีต้องปรับวันเก็บเงินหลังสัญญาจบ
 * (เช่น ลูกค้าตกลงจ่ายถึงสิ้นเดือน) แต่เดิมไม่มีทางแก้เลย เพราะหน้าแก้ไขสัญญาไม่รับสัญญาที่จบแล้ว
 *
 * - ปุ่มดินสอแสดงเฉพาะ COMPLETED / CANCELLED + มีสิทธิ์ rentals.billing_manage
 *   (สัญญาที่ยังเช่าอยู่ใช้หน้าแก้ไข / ใบแจ้งยกเลิกตามเดิม — ห้ามให้ทางลัดนี้ไปเลี่ยงขั้นตอนนั้น)
 * - ต้องกรอกเหตุผล (≥3 ตัวอักษร) — backend บันทึกลงประวัติการดำเนินการ
 * - ข้อความ error จาก backend ต้องถึงผู้ใช้ (getApiErrorMessage) และฟอร์มยังเปิดอยู่ให้แก้ต่อ
 */
import React, { useEffect, useState } from 'react';
import { RiPencilLine, RiCheckLine, RiCloseLine } from 'react-icons/ri';
import { cn } from '../../utils/cn';
import { usePermission } from '../../hooks/usePermission';
import { showSuccess, showError, showInfo } from '../../utils/alert';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import rentalService, { BILLING_MANAGE_PERMISSION } from '../../services/rentalService';

/** สถานะที่แก้ได้ผ่านตัวแก้ไขนี้ — ต้องตรงกับด่านฝั่ง backend */
export const BILLING_EDITABLE_STATUSES = ['COMPLETED', 'CANCELLED'] as const;

export const BILLING_REASON_MIN_LENGTH = 3;

export interface BillingEndDateInlineEditorProps {
  rentalId: number;
  rentalStatus: string;
  billingEndDate: string | null;
  actualReturnDate?: string | null;
  billingStartDate?: string | null;
  onSaved: () => void | Promise<void>;
  testId?: string;
}

const datePart = (v?: string | null): string => (v ? String(v).slice(0, 10) : '');

const formatThaiDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('th-TH', { year: '2-digit', month: 'long', day: 'numeric' });
};

const BillingEndDateInlineEditor: React.FC<BillingEndDateInlineEditorProps> = ({
  rentalId,
  rentalStatus,
  billingEndDate,
  actualReturnDate,
  billingStartDate,
  onSaved,
  testId = 'billing-end-date',
}) => {
  const { hasPermission } = usePermission();
  const statusOk = (BILLING_EDITABLE_STATUSES as readonly string[]).includes(rentalStatus);
  const allowed = statusOk && hasPermission(BILLING_MANAGE_PERMISSION);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dateDraft, setDateDraft] = useState(datePart(billingEndDate));
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!editing) setDateDraft(datePart(billingEndDate));
  }, [billingEndDate, editing]);

  if (!allowed) return null;

  const returnDate = datePart(actualReturnDate);
  const minDate = returnDate || datePart(billingStartDate);
  const dateValid = !!dateDraft && (!minDate || dateDraft >= minDate);
  const canSave = dateValid && reason.trim().length >= BILLING_REASON_MIN_LENGTH && !saving;

  const startEdit = () => {
    setDateDraft(datePart(billingEndDate));
    setReason('');
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setReason('');
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await rentalService.updateBillingEndDate(rentalId, {
        billing_end_date: dateDraft,
        reason: reason.trim(),
      });
      if (res && res.changed === false) {
        showInfo('ค่าเดิมไม่ได้เปลี่ยน');
      } else {
        showSuccess('บันทึกวันสิ้นสุดเก็บเงินใหม่แล้ว');
      }
      setEditing(false);
      setReason('');
      await onSaved();
    } catch (err) {
      showError(getApiErrorMessage(err, 'ไม่สามารถแก้วันสิ้นสุดเก็บเงินได้'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="ml-2 inline-flex min-h-[24px] min-w-[24px] items-center justify-center rounded text-secondary-400 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
        title="แก้วันสิ้นสุดเก็บเงิน"
        aria-label="แก้วันสิ้นสุดเก็บเงิน"
        data-testid={`${testId}-edit`}
        onClick={startEdit}
      >
        <RiPencilLine className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div
      className={cn('mt-2 space-y-2 rounded border border-secondary-200 bg-secondary-50 p-3')}
      data-testid={`${testId}-form`}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
    >
      <div>
        <input
          type="date"
          className="input text-sm py-1"
          aria-label="วันสิ้นสุดเก็บเงินใหม่"
          data-testid={`${testId}-date`}
          value={dateDraft}
          min={minDate || undefined}
          onChange={(e) => setDateDraft(e.target.value)}
          disabled={saving}
          autoFocus
        />
        {returnDate && (
          <p className="mt-1 text-xs text-secondary-500">ต้องไม่ก่อนวันคืนจริง {formatThaiDate(returnDate)}</p>
        )}
        {!dateValid && dateDraft && (
          <p className="mt-1 text-xs text-danger-600" data-testid={`${testId}-date-error`}>
            วันที่ต้องไม่ก่อน {formatThaiDate(minDate)}
          </p>
        )}
      </div>
      <textarea
        className="input text-sm py-1 w-full"
        rows={2}
        aria-label="เหตุผล"
        placeholder="เหตุผล เช่น ลูกค้าตกลงจ่ายถึงสิ้นเดือน"
        maxLength={500}
        data-testid={`${testId}-reason`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={saving}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-primary px-3 py-1 text-sm inline-flex items-center gap-1"
          data-testid={`${testId}-save`}
          onClick={() => void save()}
          disabled={!canSave}
        >
          <RiCheckLine className="h-4 w-4" />
          {saving ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
        <button
          type="button"
          className="btn btn-secondary px-3 py-1 text-sm inline-flex items-center gap-1"
          data-testid={`${testId}-cancel`}
          onClick={cancel}
          disabled={saving}
        >
          <RiCloseLine className="h-4 w-4" />
          ยกเลิก
        </button>
      </div>
    </div>
  );
};

export default BillingEndDateInlineEditor;
